import { formatNumberToAmountWithCommasAndTwoDecimals } from 'core/format';
import { DateFormat, DateService } from 'frontend-toolkit';
import { TEAMSNAP_URL } from '../../core/constants';
import { Form, FormRetiredStatus } from './RegistrationList';
import { GroupCapacityRow, GroupEligibilityRow, ParticipantGroup, GroupAutoRosterOptionGroup } from './v2/types';

export const handleExport = (exportToCsv, forms: Form[], orgName) => {
  const data = forms.map((form) => {
    const { name, isOpen, createdAt, season, participantCount, collected, balance } = form;

    return [
      name,
      isOpen ? 'Open' : 'Closed',
      DateService.dateToString(new Date(createdAt), DateFormat.DD_MM_YYYY),
      `${season.program.name} (${season.name})`,
      participantCount,
      collected || 0,
      balance || 0,
    ];
  });

  const filename = `${orgName} Registrations ${DateService.dateToString(new Date(), DateFormat.DD_MM_YYYY)}.csv`;

  exportToCsv({
    filename,
    data,
    headers: ['Name', 'Status', 'Date Created', 'Program', 'Signups', 'Collected', 'Balance'],
  });
};

export const extractFilterValues = (forms: Form[]) => {
  const filters = {
    programs: [],
    seasons: [],
    status: [
      {
        value: 'Open',
        label: 'Open',
      },
      {
        value: 'Closed',
        label: 'Closed',
      },
    ],
  };

  if (forms) {
    forms.forEach((form) => {
      const {
        season: {
          id: seasonId,
          name: seasonName,
          program: { name: programName },
        },
      } = form;

      if (!filters.programs.find((program) => program.value === programName)) {
        filters.programs.push({
          value: programName,
          label: programName,
        });
      }

      const seasonExists = filters.seasons.find((season) => season.name === seasonName);
      if (!seasonExists) {
        filters.seasons.push({
          id: seasonId,
          name: seasonName,
        });
      }
    });

    filters.programs.sort((a, b) => a.value.localeCompare(b.value));
    filters.seasons.sort((a, b) => b.id - a.id);
    filters.seasons = filters.seasons.map((season) => ({
      value: season.name,
      label: season.name,
    }));
  }

  return filters;
};

// Active form URLs go to form details page (with ID).
// Retired form URLs go to form list view (no ID).
export const generateFormUrl = (formId: number, divisionId: number, status: FormRetiredStatus) =>
  `${TEAMSNAP_URL}/divisions/${divisionId}/registration/${status === FormRetiredStatus.Active ? formId : ''}`;

/**
 * FILTERING
 */
const applyRetiredStatusFilter = (forms: Form[], status: string): Form[] => {
  const isRetired = status === 'retired';
  return forms.filter((form) => (form.isRetired && isRetired) || (!form.isRetired && !isRetired));
};

const applyDropdownsFilter = (forms: Form[] | null, filters): Form[] =>
  forms
    .filter((form) => !filters.program || !filters.program.length || filters.program.includes(form.season.program.name))
    .filter((form) => !filters.season || !filters.season.length || filters.season.includes(form.season.name))
    .filter(
      (form) => !filters.status || !filters.status.length || filters.status.includes(form.isOpen ? 'Open' : 'Closed')
    );

const applySearchFilter = (forms: Form[], searchTerm: string): Form[] => {
  if (!searchTerm?.length) {
    return forms;
  }

  searchTerm = searchTerm.toLocaleLowerCase();

  return forms.filter((form) => {
    const programSeasonName = `${form.season.program.name} (${form.season.name})`.toLocaleLowerCase();
    if (programSeasonName.includes(searchTerm)) {
      return true;
    }

    const formName = form.name.toLocaleLowerCase();
    if (formName.includes(searchTerm)) {
      return true;
    }

    return false;
  });
};

export const applyFilters = (forms: Form[], status, filter): Form[] => {
  if (!forms.length) {
    return [];
  }

  let filteredForms = applyRetiredStatusFilter(forms, status);
  filteredForms = applySearchFilter(filteredForms, filter.searchTerm);
  filteredForms = applyDropdownsFilter(filteredForms, filter);

  return filteredForms;
};

/**
 * SORTING
 */
const sortProgramSeasonField = (forms: Form[], sortAsc: boolean) =>
  forms.sort((a, b) => {
    let referenceForm = a;
    let compareForm = b;

    if (!sortAsc) {
      referenceForm = b;
      compareForm = a;
    }

    const referenceStr = `${referenceForm.season.program.name} (${referenceForm.season.name})`;
    const compareStr = `${compareForm.season.program.name} (${compareForm.season.name})`;

    return referenceStr.localeCompare(compareStr);
  });

// Map table field names to API property names.
const fieldNameSortMap = {
  dateCreated: 'createdAt',
  status: 'isOpen',
};

const sort = (forms: Form[], sortBy: string, sortAsc: boolean) => {
  // Map table columns to actual Form property names.
  sortBy = sortBy in fieldNameSortMap ? fieldNameSortMap[sortBy] : sortBy;

  return forms.sort((a, b) => {
    let referenceForm = a;
    let compareForm = b;

    // Swap the reference/compare forms if the sort is descending.
    if (!sortAsc) {
      referenceForm = b;
      compareForm = a;
    }

    const referenceField = referenceForm[sortBy];
    const compareField = compareForm[sortBy];

    if (typeof referenceField !== 'string') {
      // Sort numbers and boolean values with a simple comparison.
      return referenceField - compareField;
    } else {
      return referenceField.localeCompare(compareField);
    }
  });
};

export const applySort = (unsortedForms: Form[], sortBy: string, sortAsc: boolean): Form[] => {
  if (!unsortedForms.length) {
    return [];
  }

  // Prevent mutations on original forms collection.
  const forms = [...unsortedForms];

  // Apply default sort: date created, desc (most recent first).
  forms.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  if (sortBy) {
    // Special logic for sorting the program+season composite field.
    if (sortBy === 'programSeason') {
      return sortProgramSeasonField(forms, sortAsc);
    }

    return sort(forms, sortBy, sortAsc);
  }

  return forms;
};

export const formatGroupsForSummary = (groups) => {
  if (groups && groups.length > 1) {
    return groups.length + ' participant groups';
  } else if (groups && groups.length === 1) {
    return groups[0].name;
  } else {
    return 'None Added';
  }
};

export const formatFeesForSummary = (fees) => {
  const uniqueFeesAmountList = fees
    ?.filter((f) => f.id)
    .map((fee) => parseFloat(fee.amount))
    ?.filter((x, i, a) => a.indexOf(x) === i);

  if (uniqueFeesAmountList?.length > 1) {
    return (
      '$' +
      formatNumberToAmountWithCommasAndTwoDecimals(Math.min(...uniqueFeesAmountList)) +
      ' - $' +
      formatNumberToAmountWithCommasAndTwoDecimals(Math.max(...uniqueFeesAmountList))
    );
  } else if (uniqueFeesAmountList?.length === 1) {
    return '$' + formatNumberToAmountWithCommasAndTwoDecimals(uniqueFeesAmountList[0]);
  } else {
    return 'None Added';
  }
};

export const buildEligibilityRules = (
  rows: ParticipantGroup[],
  seasonDivisionsTeams: any,
  resetRow: boolean
): GroupEligibilityRow[] => {
  const autoRosterOptions = getValidAutoRosterOptions(seasonDivisionsTeams);

  return rows.map((row) => ({
    active: row.active,
    name: row.name,
    id: row.id,
    expanded: true,
    birthdates: {
      toggled: row.from_birthdate_range || row.to_birthdate_range ? true : false,
      start: row.from_birthdate_range,
      end: row.to_birthdate_range,
    },
    genders: {
      toggled: row.gender_options ? true : false,
      options: row.gender_options?.split(',') || [],
    },
    autoRostering: {
      toggled: resetRow ? false : row.roster_scope_id && row.roster_scope_type ? true : false,
      options: autoRosterOptions,
      rosterScopeId: resetRow ? null : row.roster_scope_id,
      rosterScopeType: resetRow ? null : row.roster_scope_type,
      rosterMode: resetRow ? 'instant' : row.roster_mode,
      error: resetRow
        ? null
        : validateRosterScopeIdInGroup(autoRosterOptions, row.roster_scope_id, row.roster_scope_type),
    },
  }));
};

export const getValidAutoRosterOptions = (seasonDivisionsTeams: any) => {
  const autoRosterOptions = [
    { label: 'Divisions', options: [] },
    { label: 'Teams', options: [] },
  ];

  seasonDivisionsTeams.forEach((item) => {
    if (item.__typename === 'Division') {
      autoRosterOptions
        .find((option) => option.label === 'Divisions')
        .options.push({ value: item.id?.toString(), label: item.name });
    }

    if (item.__typename === 'SeasonTeam') {
      autoRosterOptions
        .find((option) => option.label === 'Teams')
        .options.push({ value: item.id?.toString(), label: item.name });
    }
  });

  return autoRosterOptions;
};

export const validateRosterScopeIdInGroup = (
  options: GroupAutoRosterOptionGroup[],
  rosterScopeId: number,
  rosterScopeType: string
) => {
  let foundTeamOrDivision = null;
  if (rosterScopeId && rosterScopeType) {
    if (rosterScopeType === 'division') {
      foundTeamOrDivision = options
        .find((option) => option.label === 'Divisions')
        .options.find((x) => x.value.toString() === rosterScopeId.toString());
    } else if (rosterScopeType === 'team') {
      foundTeamOrDivision = options
        .find((option) => option.label === 'Teams')
        .options.find((x) => x.value.toString() === rosterScopeId.toString());
    }
    if (foundTeamOrDivision) {
      return null;
    } else {
      return 'Selected roster location does not exist.';
    }
  } else {
    return null;
  }
};

export const buildCapacityLimits = (rows: ParticipantGroup[]): GroupCapacityRow[] =>
  rows.map((row, index) => ({
    active: row.active,
    name: row.name,
    id: row.id,
    expanded: index === 0 ? true : false,
    toggled: !row.capacity_limit && row.capacity_limit !== 0 ? false : true,
    maxCapacity: row.capacity_limit,
    error: null,
    waitlistAllowed: row.waitlist_allowed,
  }));

export const canBeAcceptedOrReleasedFromWaitlist = (registrant) =>
  registrant?.waitlistStatus &&
  (registrant?.waitlistStatus?.toLowerCase().indexOf('waitlisted') !== -1 ||
    registrant?.waitlistStatus?.toLowerCase().indexOf('extended') !== -1 ||
    registrant?.waitlistStatus?.toLowerCase().indexOf('accepted') !== -1);

export const canBeDeleted = (status) => status === 'In Progress' || status === 'In Cart';
